import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { getSessionId, clearAllSessionData } from '../utils/session';
import './ChatAssistant.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatAssistant = ({ cart, embedded = false, onDataComplete = null, onSendToWhatsApp = null }) => {
  const [isOpen, setIsOpen] = useState(embedded);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [dataCompleteCallbackCalled, setDataCompleteCallbackCalled] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);
  const [showWhatsAppButton, setShowWhatsAppButton] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = getSessionId();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (embedded && onDataComplete && !dataCompleteCallbackCalled && messages.length > 0) {
      const checkDataComplete = async () => {
        try {
          const response = await axios.get(`${API_URL}/customer-data/${sessionId}/missing`);
          if (response.data && response.data.isComplete) {
            setDataCompleteCallbackCalled(true);
            onDataComplete();
          }
        } catch (_) { /* ignore */ }
      };
      checkDataComplete();
    }
  }, [messages, embedded, onDataComplete, sessionId, dataCompleteCallbackCalled]);

  const buildCartContext = useCallback(() => {
    if (cart.length === 0) return null;
    return {
      items: cart,
      totalPrice: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)
    };
  }, [cart]);

  const sendGreeting = useCallback(async () => {
    setIsLoading(true);
    if (embedded) {
      clearAllSessionData();
    }
    try {
      const initialMessage = embedded ? 'Quero fechar pedido' : 'Olá!';
      const response = await axios.post(`${API_URL}/chat`, {
        sessionId,
        message: initialMessage,
        cartContext: buildCartContext()
      });
      setMessages([{
        id: `greeting-${Date.now()}`,
        sessionId,
        message: response.data.message,
        sender: 'assistant',
        timestamp: new Date().toISOString()
      }]);
      setHasGreeted(true);
      if (response.data.showWhatsAppButton) {
        setShowWhatsAppButton(true);
      }
    } catch (_) {
      setMessages([{
        id: `fallback-${Date.now()}`,
        sessionId,
        message: 'Olá! Sou o assistente virtual do Empadão da Ilha! Como posso ajudar você hoje?',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      }]);
      setHasGreeted(true);
    }
    setIsLoading(false);
  }, [sessionId, embedded, buildCartContext]);

  const initializeChat = useCallback(() => {
    clearAllSessionData();
    sendGreeting();
  }, [sendGreeting]);

  useEffect(() => {
    if (isOpen && !hasGreeted && messages.length === 0) {
      sendGreeting();
    }
  }, [isOpen, hasGreeted, messages.length, sendGreeting]);

  useEffect(() => {
    if (embedded && !hasGreeted && messages.length === 0) {
      const timer = setTimeout(() => {
        initializeChat();
      }, 2000);
      return () => clearTimeout(timer);
    }
    setIsLoading(false);
  }, [embedded, hasGreeted, messages.length, initializeChat]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      sessionId,
      message: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    const msgText = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/chat`, {
        sessionId,
        message: msgText,
        cartContext: buildCartContext()
      });

      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        sessionId,
        message: response.data.message,
        sender: 'assistant',
        timestamp: new Date().toISOString()
      }]);

      if (response.data.showSendButton) setShowSendButton(true);
      if (response.data.showWhatsAppButton) setShowWhatsAppButton(true);
    } catch (_) {
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        sessionId,
        message: 'Desculpe, tive um problema. Pode tentar novamente?',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      }]);
    }
    setIsLoading(false);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (embedded) {
    return (
      <div className="chat-embedded" data-testid="chat-embedded">
        <div className="chat-messages embedded">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`chat-message ${msg.sender === 'user' ? 'user-message' : 'assistant-message'}`}
            >
              <div className="message-content">{msg.message}</div>
              <span className="message-time">{formatTime(msg.timestamp)}</span>
            </div>
          ))}
          {isLoading && (
            <div className="chat-message assistant-message">
              <div className="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showWhatsAppButton && onSendToWhatsApp && (
          <div className="whatsapp-button-container">
            <button
              onClick={() => {
                setShowWhatsAppButton(false);
                onSendToWhatsApp();
              }}
              className="whatsapp-send-button"
              data-testid="whatsapp-send-btn"
            >
              Enviar pelo WhatsApp
            </button>
          </div>
        )}

        {showSendButton && onSendToWhatsApp && (
          <div className="special-send-button-container">
            <button
              onClick={() => {
                setShowSendButton(false);
                onSendToWhatsApp();
              }}
              className="special-send-button"
              data-testid="special-send-btn"
            >
              Enviar Pedido Agora
            </button>
          </div>
        )}

        <form onSubmit={sendMessage} className="chat-input-form embedded">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="chat-input"
            disabled={isLoading}
            data-testid="chat-input"
          />
          <button
            type="submit"
            className="chat-send-button"
            disabled={!inputMessage.trim() || isLoading}
            data-testid="chat-send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    );
  }

  return (
    <>
      {!isOpen && (
        <button
          className="chat-float-button"
          onClick={() => setIsOpen(true)}
          aria-label="Abrir chat"
          title="Fale com o Chef Empada!"
          data-testid="chat-float-btn"
        >
          <div className="chef-icon">Chef</div>
          <span className="chat-badge-pulse"></span>
        </button>
      )}

      {isOpen && (
        <div className={`chat-window ${isMinimized ? 'minimized' : ''}`} data-testid="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-avatar">Chef</div>
              <div className="chat-header-text">
                <h3>Chef Empada</h3>
                <span className="chat-status">Pronto para ajudar!</span>
              </div>
            </div>
            <div className="chat-header-actions">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="chat-header-button"
                aria-label="Minimizar"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="chat-header-button"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div className="chat-messages">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-message ${msg.sender === 'user' ? 'user-message' : 'assistant-message'}`}
                  >
                    <div className="message-bubble">
                      <p>{msg.message}</p>
                      <span className="message-time">{formatTime(msg.timestamp)}</span>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="chat-message assistant-message">
                    <div className="message-bubble typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={sendMessage} className="chat-input-container">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Digite sua mensagem..."
                  className="chat-input"
                  disabled={isLoading}
                  data-testid="chat-input-floating"
                />
                <Button
                  type="submit"
                  disabled={!inputMessage.trim() || isLoading}
                  className="chat-send-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatAssistant;
